// --- FINAL & CORRECTED FILE: packages/components/src/admin/sprints/SprintList.tsx ---

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDroppable } from '@dnd-kit/core';
import { Plus, Calendar, Flag } from 'lucide-react';
import clsx from 'clsx';
import { motion } from 'framer-motion';

import { Button, Badge, Tooltip } from '@dopomogai/ui';
import type { Sprint } from '@dopomogai/types';

// --- Redesigned Internal List Item Component ---
interface SprintListItemProps {
  sprint: Sprint;
  isSelected: boolean;
  onSelect: () => void;
  index: number;
}

const SprintListItem: React.FC<SprintListItemProps> = ({ sprint, isSelected, onSelect, index }) => {
  const { t } = useTranslation('admin_sprints');
  
  // --- FIX: Use a unique and parsable ID for the droppable area. ---
  // This allows the parent DND context to identify the target sprint.
  const { setNodeRef, isOver } = useDroppable({
    id: `sprint-drop-zone-${sprint.id}`,
    data: { sprintId: sprint.id }, // Pass extra data if needed
  });

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const statusColors: { [key: string]: 'green' | 'blue' | 'gray' | 'yellow' } = {
    Completed: 'green',
    Active: 'blue',
    Planning: 'gray',
    Archived: 'yellow',
  };

  return (
    <motion.li
      ref={setNodeRef} // Dnd-kit ref for the droppable area
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
    >
      <button
        onClick={onSelect}
        className={clsx(
          'w-full text-left p-3 rounded-lg border-2 bg-white',
          'transition-all duration-200 transform',
          // Standard neobrutalist hover/active effects
          'hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[4px_4px_0_0_rgb(17,24,39)]',
          'active:translate-x-0 active:translate-y-0 active:shadow-none',
          // Conditional styling for selection and drag-over states
          isSelected 
            ? 'border-emerald-500 ring-2 ring-emerald-200' 
            : 'border-black',
          isOver && 'border-dashed !border-sky-500 bg-sky-50', // !border-sky-500 to override other border colors
        )}
        aria-current={isSelected ? 'true' : 'false'}
      >
        <div className="flex justify-between items-start mb-2">
          <h4 className="font-bold text-slate-800 pr-2">{sprint.name}</h4>
          <Badge text={sprint.status} accentColor={statusColors[sprint.status] || 'gray'} />
        </div>
        <p className="text-xs text-slate-500 mt-1 truncate" title={sprint.goal || ''}>
          <Flag size={12} className="inline-block align-middle mr-1.5" />
          {sprint.goal || t('noGoalSet')}
        </p>
        <div className="text-xs text-slate-500 mt-2 flex items-center gap-2">
          <Calendar size={12} />
          <span>{formatDate(sprint.start_date)} - {formatDate(sprint.end_date)}</span>
        </div>
      </button>
    </motion.li>
  );
};


// --- Internal Skeleton Component ---
const SprintListSkeleton: React.FC = () => (
    <div className="space-y-4 animate-pulse">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="rounded-lg bg-slate-200 h-[104px]" />
      ))}
    </div>
  );
  

// --- Main Exported Component ---
interface SprintListProps {
  sprints: Sprint[];
  selectedSprintId: string | null;
  onSelectSprint: (sprintId: string) => void;
  onCreateSprint: () => void;
  isLoading?: boolean;
}

/**
 * A panel that displays a list of sprints for a project. It handles
 * loading states, selection, creation, and acts as a drop zone for tasks.
 */
export const SprintList: React.FC<SprintListProps> = ({
  sprints,
  selectedSprintId,
  onSelectSprint,
  onCreateSprint,
  isLoading,
}) => {
  const { t } = useTranslation('admin_sprints');

  const renderContent = () => {
    if (isLoading) {
      return <SprintListSkeleton />;
    }

    if (sprints.length === 0) {
      return (
        <div className="text-center p-4 border-2 border-dashed rounded-xl bg-slate-50">
          <p className="text-sm font-semibold text-slate-600">{t('noSprints.title')}</p>
          <p className="text-xs text-slate-500 mt-1">{t('noSprints.description')}</p>
        </div>
      );
    }

    return (
      <ul className="space-y-4">
        {sprints.map((sprint, index) => (
          <SprintListItem
            key={sprint.id}
            sprint={sprint}
            isSelected={sprint.id === selectedSprintId}
            onSelect={() => onSelectSprint(sprint.id)}
            index={index}
          />
        ))}
      </ul>
    );
  };

  return (
    <div className="h-full bg-slate-100 border-r-2 border-black flex flex-col">
      <header className="p-4 border-b-2 border-black flex justify-between items-center flex-shrink-0 bg-white">
        <h3 className="text-xl font-bold text-slate-800">{t('sprintsTitle')}</h3>
        <Tooltip textKey="admin_sprints:newSprintTooltip">
          <Button
            icon={<Plus size={16} />}
            onClick={onCreateSprint}
            variant="secondary"
            size="sm"
            textKey="common:new"
          />
        </Tooltip>
      </header>
      <div className="p-4 overflow-y-auto flex-grow">
        {renderContent()}
      </div>
    </div>
  );
};